import React, { useEffect } from 'react';
import { MapContainer, TileLayer, Polygon, Marker, Popup, Polyline, CircleMarker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom icon for centroid
const centroidIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

function FitBounds({ geojson }) {
  const map = useMap();
  
  useEffect(() => {
    if (geojson) {
      try {
        const geoLayer = L.geoJSON(geojson);
        const bounds = geoLayer.getBounds();
        if (bounds.isValid()) {
          map.fitBounds(bounds, { padding: [50, 50] });
        }
      } catch (e) {
        console.error('Error fitting bounds:', e);
      }
    }
  }, [geojson, map]);
  
  return null;
}

// Convert GeoJSON coordinates to Leaflet format [lat, lng]
const convertCoords = (coords) => {
  return coords.map(coord => [coord[1], coord[0]]);
};

// Render polygons from GeoJSON (supports Polygon and MultiPolygon)
const GeoJSONPolygons = ({ geojson }) => {
  if (!geojson) return null;

  const polygonStyle = {
    color: '#0ea5e9',
    fillColor: '#0ea5e9',
    fillOpacity: 0.12,
    weight: 2
  };

  if (geojson.type === 'Polygon') {
    const positions = geojson.coordinates.map(ring => convertCoords(ring));
    return <Polygon positions={positions} pathOptions={polygonStyle} />;
  }

  if (geojson.type === 'MultiPolygon') {
    return (
      <>
        {geojson.coordinates.map((polygon, idx) => {
          const positions = polygon.map(ring => convertCoords(ring));
          return <Polygon key={idx} positions={positions} pathOptions={polygonStyle} />;
        })}
      </>
    );
  }

  return null;
};

// Visualization: lines from key points to centroid
const CentroidVisualization = ({ centroid, keyPoints }) => {
  if (!centroid || !keyPoints || keyPoints.length === 0) return null;
  
  const lineStyle = {
    color: '#ef4444',
    weight: 1.5,
    opacity: 0.6,
    dashArray: '4, 4'
  };

  return (
    <>
      {/* Lines from key points to centroid */}
      {keyPoints.map((point, idx) => (
        <Polyline
          key={`line-${idx}`}
          positions={[
            [point.lat, point.lng],
            [centroid.lat, centroid.lng]
          ]}
          pathOptions={lineStyle}
        />
      ))}
      
      {/* Key points markers */}
      {keyPoints.map((point, idx) => (
        <CircleMarker
          key={`point-${idx}`}
          center={[point.lat, point.lng]}
          radius={4}
          pathOptions={{
            color: '#f97316',
            fillColor: '#f97316',
            fillOpacity: 1,
            weight: 1
          }}
        />
      ))}
    </>
  );
};

const MapView = ({ geojson, centroid, keyPoints = [] }) => {
  const defaultCenter = [55.7558, 37.6173];
  const defaultZoom = 5;

  return (
    <MapContainer
      center={defaultCenter}
      zoom={defaultZoom}
      className="w-full h-full"
      style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
      zoomControl={false}
      attributionControl={false}
    >
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      
      {geojson && (
        <>
          <GeoJSONPolygons geojson={geojson} />
          <FitBounds geojson={geojson} />
        </>
      )}
      
      {/* Visualization: lines to centroid */}
      <CentroidVisualization centroid={centroid} keyPoints={keyPoints} />
      
      {centroid && (
        <Marker position={[centroid.lat, centroid.lng]} icon={centroidIcon}>
          <Popup>
            <div className="text-center">
              <strong className="text-red-600">Центроид</strong>
              <br />
              Широта: {centroid.lat.toFixed(6)}
              <br />
              Долгота: {centroid.lng.toFixed(6)}
              <br />
              <span className="text-xs text-slate-500 mt-1 block">
                Центр тяжести полигона
              </span>
            </div>
          </Popup>
        </Marker>
      )}
    </MapContainer>
  );
};

export default MapView;
